import { Slot } from 'radix-ui';
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactElement } from 'react';

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?:
    | 'primary'
    | 'yellow'
    | 'close'
    | 'green'
    | 'ghost'
    | 'navy'
    | 'maroon'
    | 'teal2'
    | 'outline'
    | 'white'
    | 'link'
    | 'link-sm'
    | 'link-on-dark'
    | 'danger'
    | 'dashed'
    | 'admin'
    | 'chip-green'
    | 'chip-outline'
    | 'adminPrimary'
    | 'adminOutline'
    | 'linkDanger'
    | 'chip-white'
    | 'field-outline'
    | 'outline-sm'
    | 'outline-sm-accent'
    | 'outline-md'
    | 'outline-light'
    | 'outline-danger';
  asChild?: boolean;
}

// Variants with no background of their own (bare text/icon buttons) — a solid
// disabled fill on top of their zero/small padding produces a cramped grey
// pill instead of a dim, still-legible label, so they get a text-only
// disabled treatment instead of the shared bg-slate fill below.
// `field-outline` is here too: it has a border but no fill, and a grey block
// beside an empty promo field read as broken rather than as "type first".
const TEXT_VARIANTS = new Set<NonNullable<ButtonProps['variant']>>([
  'link',
  'link-sm',
  'link-on-dark',
  'ghost',
  'linkDanger',
  'field-outline',
]);

// Each variant owns its full box model (radius/padding/weight), not just
// color — ui-guidelines.md §3 gives distinct button types distinct shapes
// (16px-radius 16/32-padding buttons vs. the pill-shaped ghost/link vs. the
// 14px close button), so a shared base padding would be wrong for several
// of them.
const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
  // Primary Button (§3)
  primary: 'rounded-button px-8 py-4 font-medium bg-dkgreen hover:bg-dkgreen-hover text-white',
  // Primary Yellow Button (§3)
  yellow:  'rounded-button px-8 py-4 font-medium bg-yellow hover:bg-yellow/90 text-navy',
  // Close Button (§3) — icon-only, no label weight in spec
  close:   'rounded-[0.875rem] p-3.5 bg-white border border-teal/15 text-navy',
  // Brighter brand green used for the newer action CTAs (Invite / Send Invite,
  // Figma teams-and-permissions). Darkens to dkgreen on hover. Not in
  // ui-guidelines.md — kept for existing call sites, same box model as Primary.
  green:   'rounded-button px-8 py-4 font-medium bg-green hover:bg-dkgreen text-white',
  // Tertiary / Text Button (§3)
  ghost:   'rounded-full px-2.5 py-2.5 font-medium bg-transparent text-navy hover:text-dkgreen',
  navy:    'rounded-button px-8 py-4 font-medium bg-navy hover:bg-teal text-white',
  // Investors page brand accent (MYN-255/MYN-214) - #4F0A33 (`--color-maroon`),
  // same box model as Primary. A one-page accent, not a reusable semantic
  // variant, but still needs its own variant rather than a className override:
  // Button concatenates `className` after VARIANT_CLASSES in a plain string,
  // not a merge utility, so an override can't reliably win against the
  // variant's own `bg-*` class on CSS specificity/rule-order grounds.
  maroon:  'rounded-button px-8 py-4 font-medium bg-maroon hover:bg-maroon/90 text-white',
  // Trusted Partners page brand accent (MYN-216/MYN-215, trusted_p_ch.docx) -
  // #2B958E (`--color-teal2`), same box model as Primary. A one-page accent,
  // same reasoning as `maroon` above.
  teal2:   'rounded-button px-8 py-4 font-medium bg-teal2 hover:bg-teal2/90 text-white',
  // Secondary Button (§3). Themed via btn-outline-* tokens whose light values
  // are the literal navy/white pair this used to hardcode, so member screens
  // render identically; the admin console's dark mode swaps them for the
  // hairline + primary-text pair (navy-on-Ink was invisible).
  outline:
    'rounded-button px-8 py-4 font-medium border border-btn-outline-border text-btn-outline-text hover:bg-btn-outline-hover-bg hover:text-btn-outline-hover-text',
  white:   'rounded-button px-8 py-4 font-medium border border-slate bg-white text-navy hover:border-navy',
  // Link (§3)
  link:    'rounded-full p-0 font-semibold bg-transparent text-dkgreen hover:text-green',
  // 14px medium green text action on a profile card row (Add link, MYN-217).
  'link-sm': 'shrink-0 rounded-full p-0 text-sm font-medium bg-transparent text-dkgreen hover:text-dkgreen-hover',
  // Bright green text action on a navy card (Create new recovery codes, MYN-217).
  'link-on-dark': 'w-fit rounded-full p-0 text-sm font-semibold bg-transparent text-green hover:text-green/80',
  danger:  'rounded-button px-8 py-4 font-medium bg-red hover:bg-red/90 text-white',
  dashed:  'rounded-button px-8 py-4 border-2 border-dashed border-green text-dkgreen hover:bg-green/5',
  // Admin toolbar control - compact padding for console toolbars and bulk bars.
  // Themed, so it follows the admin shell's data-theme rather than being a fixed
  // dark control; every call site is under /admin/*, so no member screen sees it.
  admin:
    'rounded-xl px-5 py-2.5 text-sm font-medium bg-surface-raised hover:bg-row-hover text-text-primary border border-border-strong',
  // Compact pill for inline list-row actions (e.g. event Register/Unregister) -
  // named by shape/size, not domain, same as every other variant here. Distinct
  // box model from every variant above (px-8 py-4 rounded-button), so it can't
  // reuse `green`/`danger`.
  'chip-green': 'rounded-[0.375rem] px-3 py-1.5 font-medium bg-green hover:bg-dkgreen text-white',
  // 13px/7px (vs. chip-green's 12px/6px) - Figma pads this one slightly wider
  // to keep the label clear of the added 1px border.
  'chip-outline':
    'rounded-[0.375rem] px-[0.8125rem] py-[0.4375rem] font-medium border border-red bg-white text-red hover:bg-red/5',
  // Admin primary control - a real filled CTA for drawer/dialog header strips
  // (a form Save, a dialog confirm), which `admin` was never meant to carry:
  // that variant is a toolbar/bulk-bar control (see its own comment above),
  // and its near-white light-mode fill reads as disabled when asked to be the
  // primary action. Themed via the same `--accent`/`--accent-contrast` tokens
  // Pagination's active-page state already uses, so it follows the admin
  // shell's data-theme instead of a fixed color - `--accent-contrast` exists
  // specifically because white-on-accent is unreadable in dark mode (2.45:1).
  // Box model matches `admin` (rounded-xl px-5 py-2.5 text-sm font-medium) so
  // the two sit together at one size rather than the member-screen px-8 py-4.
  adminPrimary:
    'rounded-xl px-5 py-2.5 text-sm font-medium bg-accent hover:bg-accent/90 active:bg-accent/80 text-accent-contrast',
  // Admin secondary control - the compact, outlined counterpart to
  // `adminPrimary` for the same header strips (Cancel beside Save/confirm).
  // Same box model as `admin`/`adminPrimary`, so a Cancel next to a primary
  // action reads as one sized pair instead of the member-screen `outline`'s
  // px-8 py-4. Reuses `outline`'s already-themed btn-outline-* tokens (dark
  // mode's hairline + primary-text pair), not a new hardcoded palette.
  adminOutline:
    'rounded-xl px-5 py-2.5 text-sm font-medium border border-btn-outline-border text-btn-outline-text hover:bg-btn-outline-hover-bg hover:text-btn-outline-hover-text',
  // Plain-text red link - the destructive counterpart to `link` (MYN-196's
  // page editor: "Discard Changes" is a bare red text action with no
  // button box, per its Figma mockup). Same box model as `link`; color
  // reuses the app's one existing `--color-red` danger token (already used
  // by the member-screen `danger` variant) rather than a new hardcoded hex.
  linkDanger: 'rounded-full p-0 text-[13px] font-semibold bg-transparent text-red hover:text-red/80',
  // Small white rounded chip with a colored label (MYN-196 page editor's
  // "Save & Publish", which - unlike its "Discard Changes" sibling above -
  // Figma gives a real bounded shape, not bare text). Exact px values match
  // the Figma frame's own padding/radius rather than the nearest Tailwind
  // step, since the whole point of this variant is pixel parity with it.
  'chip-white': 'rounded-[8px] px-[20px] py-[10px] text-[13px] font-semibold bg-white text-green hover:bg-green/5',
  // Compact outline action on a member card header (profile section Edit,
  // MYN-217). Hairline border rather than `outline`'s navy one so several per
  // page do not compete with the page's primary actions.
  'outline-sm':
    'inline-flex min-h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-medium border border-panel-border bg-white text-teal hover:border-dkgreen hover:text-dkgreen active:opacity-90',
  // Green twin of `outline-sm` for an empty card's call to action (+ Add Geography, MYN-217).
  'outline-sm-accent':
    'inline-flex min-h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-medium border border-dkgreen bg-white text-dkgreen hover:bg-dkgreen/5 active:bg-dkgreen/10',
  // Navy outline action on a white profile card (Update Email, Change
  // Password; MYN-217 approved mockup `.btn-o`: 44px tall, 20px sides, 14px).
  'outline-md':
    'inline-flex min-h-11 items-center gap-2 rounded-button px-5 text-sm font-medium border border-navy bg-white text-navy hover:bg-navy hover:text-white active:opacity-90',
  // Destructive outline on a white surface (Delete Account, MYN-217).
  'outline-danger':
    'inline-flex min-h-11 items-center gap-2 rounded-button px-5 text-sm font-medium border border-red bg-white text-danger-text hover:bg-red/5 active:bg-red/10',
  // Outline action on the dark navy profile header (Export as PDF, MYN-217).
  'outline-light':
    'inline-flex min-h-11 items-center gap-2 rounded-button px-5 text-sm font-medium border border-white/40 bg-transparent text-white hover:bg-white/10 active:bg-white/15',
  // An action that sits beside a text field (e.g. a promo code's Apply). Takes
  // the Input's own box model - radius, vertical padding, border, text size -
  // so the two are the same height side by side; the member-screen `outline`
  // (px-8 py-4) towered over the field. Colours reuse `outline`'s themed tokens.
  'field-outline':
    'shrink-0 rounded-input border px-5 py-2 text-input font-medium border-btn-outline-border text-btn-outline-text hover:bg-btn-outline-hover-bg hover:text-btn-outline-hover-text active:opacity-90',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', asChild = false, type = 'button', className = '', children, ...props },
  ref,
) {
  const disabledClasses = TEXT_VARIANTS.has(variant)
    ? 'disabled:text-slate disabled:cursor-not-allowed disabled:pointer-events-none'
    : // Disabled state (§3 Rules): a solid fill, not a translucent dim — the
      // `disabled:` pseudo-class variant always outranks a variant's own plain
      // `bg-*`/`text-*` class in specificity, so this is safe regardless of
      // which filled variant is active.
      //
      // The fill is a token, not `bg-slate`: light mode resolves to that same
      // #A0AEBC, but on the admin console's dark theme a light-grey fill made
      // the disabled button the brightest element on screen (reported against
      // the suspend modal). Dark mode resolves it to a recessed fill instead.
      'disabled:bg-btn-disabled-bg disabled:text-btn-disabled-text disabled:cursor-not-allowed disabled:pointer-events-none';

  const classes =
    `transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dkgreen ${disabledClasses} ${VARIANT_CLASSES[variant]} ${className}`.trim();

  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...props}>
        {children as ReactElement}
      </Slot.Root>
    );
  }

  return (
    <button ref={ref} type={type} className={classes} {...props}>
      {children}
    </button>
  );
});
